import { GET_LIST, GET_ONE, CREATE, UPDATE, fetchUtils } from 'react-admin';
import queryString from 'query-string';
import { getApiUrlForResource } from '../services/apiUrlResolver';
import logger from '../utils/logger';
import auth from '../auth/auth0';
export const LEADS_RESOURCE_NAME = 'leads';

// Helper function to get API URL at request time
const getApiUrl = () => getApiUrlForResource(LEADS_RESOURCE_NAME);

export const prepareFilters = (filters) => {
  if (!filters) {
    return [{ name: 'isArchived', comparison: 'eq', value: false }];
  }

  const createEqFilter = (name, value) => ({ name, comparison: 'eq', value });
  const createContainsFilter = (name, value) => ({
    name,
    comparison: 'contains',
    value,
  });
  const createDateBetweenFromFilter = (name, value) => ({
    name,
    comparison: 'betweenFrom',
    value,
  });
  const createDateBetweenToFilter = (name, value) => ({
    name,
    comparison: 'betweenTo',
    value,
  });

  const resultFilters = [];

  if (filters.q) {
    if (filters.q?.includes('@')) {
      resultFilters.push(createContainsFilter('email', filters.q));
    } else {
      resultFilters.push(createContainsFilter('name', filters.q));
    }
  }

  if (filters.status) {
    resultFilters.push(createEqFilter('status', filters.status));
  }

  if (filters.source_org_id) {
    resultFilters.push(createEqFilter('sourceOrgId', filters.source_org_id));
  }

  if (filters.sms_opt_in !== undefined) {
    resultFilters.push(createEqFilter('sms_opt_in', filters.sms_opt_in));
  }

  if (filters.error_flag !== undefined) {
    resultFilters.push(createEqFilter('error_flag', filters.error_flag));
  }

  if (filters.importBatchId) {
    resultFilters.push(createEqFilter('importBatchId', filters.importBatchId));
  }

  // Archived leads handling:
  // - Default behavior: exclude archived leads
  // - If user toggles "Include archived": do not apply archive-state filter
  if (filters.includeArchived !== true) {
    resultFilters.push(createEqFilter('isArchived', false));
  }

  if (filters.createdBetween) {
    const { gte, lte } = filters.createdBetween;
    if (gte) {
      resultFilters.push(createDateBetweenFromFilter('createdAtFrom', gte));
    }
    if (lte) {
      resultFilters.push(createDateBetweenToFilter('createdAtTo', lte));
    }
  }

  return resultFilters;
};

export const fetchLeadsAnalytics = async (
  filters,
  requestOptions?: {
    signal?: AbortSignal;
  },
) => {
  const preparedFilters = prepareFilters(filters);
  const query = {
    filters: JSON.stringify(preparedFilters),
  };
  // Analytics endpoint only exists in V2 — always use V2 URL to avoid
  // GrowthBook initialization race condition falling back to V1.
  const V2_API_URL = import.meta.env.VITE_API_V2_URL;
  const url = `${V2_API_URL}/${LEADS_RESOURCE_NAME}/analytics?${queryString.stringify(query)}`;

  try {
    if (auth.isAuthenticated()) {
      auth.renewSessionIfExpiringSoon();
    } else {
      auth.logout();
      window.location.reload();
      throw new Error('Not authenticated');
    }

    const token = auth.getIdToken();
    const options = {
      headers: new Headers({
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      }),
      signal: requestOptions?.signal,
    };

    const response = await fetchUtils.fetchJson(url, options);
    return response.json;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error('Error fetching leads analytics', { errorMessage });
    throw error;
  }
};

const providerRequestToHttpRequest = (requestType, requestParams) => {
  switch (requestType) {
    case GET_LIST: {
      const { page, perPage } = requestParams.pagination;
      const { field, order } = requestParams.sort;
      const meta = requestParams.meta || {};

      // Check if this is an export request
      const isExport = meta.export === true;
      const preparedFilters = prepareFilters(requestParams.filter);

      const query = {
        sort: JSON.stringify([field, order]),
        filters: JSON.stringify(preparedFilters),
        export: isExport ? 'true' : undefined,
        maxResults: isExport ? meta.maxResults : undefined,
        // For exports, use perPage format (JSON stringified range array)
        perPage: isExport ? JSON.stringify([(page - 1) * perPage, page * perPage]) : undefined,
        offset: isExport ? (page - 1) * perPage : undefined,
        cursor: isExport ? JSON.stringify(requestParams.cursor) : undefined,
        // For regular pagination, use range format (consistent with other data providers)
        range: !isExport ? JSON.stringify([(page - 1) * perPage, page * perPage]) : undefined,
      };

      const url = `${getApiUrl()}/${LEADS_RESOURCE_NAME}?${queryString.stringify(query)}`;

      // IMPORTANT (HIPAA): never log filter values (may contain email/name/phone).
      logger.log('Leads API Request:', {
        page,
        perPage,
        isExport,
        sort: [field, order],
        filterNames: preparedFilters.map((f) => f.name),
        filterCount: preparedFilters.length,
        maxResults: meta.maxResults,
        offset: query.offset,
      });

      return { url };
    }

    case GET_ONE:
      logger.log('Leads DataProvider: GET_ONE request for ID:', requestParams.id);
      return {
        url: `${getApiUrl()}/${LEADS_RESOURCE_NAME}/${requestParams.id}`,
      };

    case CREATE:
      return {
        url: `${getApiUrl()}/${LEADS_RESOURCE_NAME}`,
        options: {
          method: 'POST',
          body: JSON.stringify(requestParams.data),
        },
      };

    case UPDATE:
      return {
        url: `${getApiUrl()}/${LEADS_RESOURCE_NAME}/${requestParams.id}`,
        options: {
          method: 'PUT',
          body: JSON.stringify(requestParams.data),
        },
      };

    default:
      throw new Error(`Unsupported fetch action type ${requestType}`);
  }
};

const httpResponseToProviderData = (response, type, params) => {
  const { headers, json } = response;

  switch (type) {
    case GET_LIST:
      // Check if this was an export request (no Content-Range header)
      const isExport = params?.meta?.export === true;
      const contentRange = headers.get('content-range');

      if (isExport || !contentRange) {
        // Export response: API may return array directly or { data, total, exported }
        const data = Array.isArray(json) ? json : (json?.data ?? []);
        const total = Array.isArray(json)
          ? json.length
          : (typeof json?.total === 'number' ? json.total : data.length);
        return { data, total };
      } else {
        // Regular pagination response with Content-Range header
        return {
          data: json,
          total: parseInt(contentRange.split('/').pop()),
        };
      }

    case GET_ONE:
      return { data: json };

    case CREATE:
      return { data: json };

    case UPDATE:
      return { data: json };

    default:
      return { data: json };
  }
};

const providerInterface = {
  providerRequestToHttpRequest,
  httpResponseToProviderData,
};

export default {
  resource: LEADS_RESOURCE_NAME,
  providerInterface,
};
